/**
 * Top-level router.
 *
 * Light public pages (landing, pricing, auth, legal, public report) are
 * imported statically so first-paint stays in a single chunk. Authenticated
 * pages, lower-traffic flows, and the heavyweight guides (react-markdown +
 * remark-gfm) are lazy-loaded — keeps the marketing bundle lean for SEO +
 * Lighthouse, and pulls in those surfaces only on navigation.
 */
import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { AuthGuard } from './lib/auth/AuthGuard';
import { ScrollToTop } from './components/ScrollToTop';
import { AnalyticsRouteTracker } from './components/AnalyticsRouteTracker';
import { CookieBanner } from './components/CookieBanner';
import { LandingPage } from './pages/LandingPage';
import { AuthPage } from './pages/AuthPage';
import { PricingPage } from './pages/PricingPage';
import { PublicReportPage } from './pages/PublicReportPage';
import { TermsPage, PrivacyPage, AcceptableUsePage, DpaPage, CookiePage } from './pages/LegalPages';
import { InvitePage } from './pages/InvitePage';
import { ContactPage } from './pages/ContactPage';
import { ApiDocsPage } from './pages/ApiDocsPage';
import { DevelopersPage } from './pages/DevelopersPage';
import { StatusPage } from './pages/StatusPage';

// Guides are public but heavy — 2,100+ lines of inlined long-form content
// plus react-markdown + remark-gfm for the AI-published guides. Lazy-load
// them so the landing/marketing chunk doesn't ship the markdown stack.
const GuidesIndexPage = lazy(() =>
  import('./pages/GuidesPage').then((m) => ({ default: m.GuidesIndexPage })),
);
const GuideDetailPage = lazy(() =>
  import('./pages/GuidesPage').then((m) => ({ default: m.GuideDetailPage })),
);
const SolutionsIndexPage = lazy(() =>
  import('./pages/SolutionsPage').then((m) => ({ default: m.SolutionsIndexPage })),
);
const SolutionDetailPage = lazy(() =>
  import('./pages/SolutionsPage').then((m) => ({ default: m.SolutionDetailPage })),
);
const OnboardingPage = lazy(() =>
  import('./pages/OnboardingPage').then((m) => ({ default: m.OnboardingPage })),
);
const DashboardPage = lazy(() =>
  import('./pages/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const PortfolioPage = lazy(() =>
  import('./pages/PortfolioPage').then((m) => ({ default: m.PortfolioPage })),
);
const DomainsPage = lazy(() =>
  import('./pages/DomainsPage').then((m) => ({ default: m.DomainsPage })),
);
const DomainDetailPage = lazy(() =>
  import('./pages/DomainDetailPage').then((m) => ({ default: m.DomainDetailPage })),
);
const AlertsPage = lazy(() =>
  import('./pages/AlertsPage').then((m) => ({ default: m.AlertsPage })),
);
const AccountPage = lazy(() =>
  import('./pages/AccountPage').then((m) => ({ default: m.AccountPage })),
);
const AdminPage = lazy(() =>
  import('./pages/AdminPage').then((m) => ({ default: m.AdminPage })),
);
const AdminOrgDetailPage = lazy(() =>
  import('./pages/AdminOrgDetailPage').then((m) => ({ default: m.AdminOrgDetailPage })),
);
const AdminAiGuidesPage = lazy(() =>
  import('./pages/AdminAiGuidesPage').then((m) => ({ default: m.AdminAiGuidesPage })),
);
const AdminSupportTicketsPage = lazy(() =>
  import('./pages/AdminSupportTicketsPage').then((m) => ({ default: m.AdminSupportTicketsPage })),
);
const AdminScanInsightsPage = lazy(() =>
  import('./pages/AdminScanInsightsPage').then((m) => ({ default: m.AdminScanInsightsPage })),
);
const AdminXPostsPage = lazy(() =>
  import('./pages/AdminXPostsPage').then((m) => ({ default: m.AdminXPostsPage })),
);
const PostmasterCallback = lazy(() =>
  import('./pages/PostmasterCallback').then((m) => ({ default: m.PostmasterCallback })),
);
const CheckoutPage = lazy(() =>
  import('./pages/CheckoutPage').then((m) => ({ default: m.CheckoutPage })),
);
const SettingsBillingPage = lazy(() =>
  import('./pages/SettingsBillingPage').then((m) => ({ default: m.SettingsBillingPage })),
);
const BillingSuccessPage = lazy(() =>
  import('./pages/BillingPage').then((m) => ({ default: m.BillingSuccessPage })),
);
const BillingCancelPage = lazy(() =>
  import('./pages/BillingPage').then((m) => ({ default: m.BillingCancelPage })),
);

function RouteFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin" />
    </div>
  );
}

export function App() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <ScrollToTop />
      <AnalyticsRouteTracker />
      <CookieBanner />
      <Routes>
        {/* Public */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/auth" element={<AuthPage />} />
        {/* Public shareable report — no auth required, token is the credential. */}
        <Route path="/r/:token" element={<PublicReportPage />} />

        {/* Legal — required by Stripe checkout, GDPR, and email-footer links. */}
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/dpa" element={<DpaPage />} />
        <Route path="/cookies" element={<CookiePage />} />
        <Route path="/acceptable-use" element={<AcceptableUsePage />} />

        {/* Public deliverability guides. */}
        <Route path="/guides" element={<GuidesIndexPage />} />
        <Route path="/guides/:slug" element={<GuideDetailPage />} />

        <Route path="/solutions" element={<SolutionsIndexPage />} />
        <Route path="/solutions/:slug" element={<SolutionDetailPage />} />

        {/* Contact form + public API documentation. */}
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/api-docs" element={<ApiDocsPage />} />
        {/* Canonical developer / AI-agent / MCP hub. */}
        <Route path="/developers" element={<DevelopersPage />} />
        {/* Service status — presents /status.json (real component health). */}
        <Route path="/status" element={<StatusPage />} />

        {/* Authenticated */}
        <Route
          path="/onboarding"
          element={
            <AuthGuard>
              <OnboardingPage />
            </AuthGuard>
          }
        />
        <Route
          path="/dashboard"
          element={
            <AuthGuard>
              <DashboardPage />
            </AuthGuard>
          }
        />
        <Route
          path="/portfolio"
          element={
            <AuthGuard>
              <PortfolioPage />
            </AuthGuard>
          }
        />
        <Route
          path="/domains"
          element={
            <AuthGuard>
              <DomainsPage />
            </AuthGuard>
          }
        />
        <Route
          path="/domains/:id"
          element={
            <AuthGuard>
              <DomainDetailPage />
            </AuthGuard>
          }
        />
        <Route
          path="/alerts"
          element={
            <AuthGuard>
              <AlertsPage />
            </AuthGuard>
          }
        />
        <Route
          path="/settings"
          element={
            <AuthGuard>
              <AccountPage />
            </AuthGuard>
          }
        />
        <Route
          path="/settings/billing"
          element={
            <AuthGuard>
              <SettingsBillingPage />
            </AuthGuard>
          }
        />
        {/* Legacy /account links → /settings (keeps deep links working). */}
        <Route path="/account" element={<Navigate to="/settings" replace />} />
        {/* Email-footer "Manage preferences" deep-link — scrolls to the email-subs card. */}
        <Route
          path="/account/notifications"
          element={<Navigate to="/settings#email-subscriptions" replace />}
        />
        <Route
          path="/admin"
          element={
            <AuthGuard requireAppRole="admin">
              <AdminPage />
            </AuthGuard>
          }
        />
        <Route
          path="/admin/orgs/:id"
          element={
            <AuthGuard requireAppRole="admin">
              <AdminOrgDetailPage />
            </AuthGuard>
          }
        />
        <Route
          path="/admin/guides"
          element={
            <AuthGuard requireAppRole="admin">
              <AdminAiGuidesPage />
            </AuthGuard>
          }
        />
        <Route
          path="/admin/support-tickets"
          element={
            <AuthGuard requireAppRole="admin">
              <AdminSupportTicketsPage />
            </AuthGuard>
          }
        />
        <Route
          path="/admin/scan-insights"
          element={
            <AuthGuard requireAppRole="admin">
              <AdminScanInsightsPage />
            </AuthGuard>
          }
        />
        <Route
          path="/admin/x-posts"
          element={
            <AuthGuard requireAppRole="admin">
              <AdminXPostsPage />
            </AuthGuard>
          }
        />
        <Route
          path="/postmaster/callback"
          element={
            <AuthGuard>
              <PostmasterCallback />
            </AuthGuard>
          }
        />
        <Route
          path="/billing/checkout"
          element={
            <AuthGuard>
              <CheckoutPage />
            </AuthGuard>
          }
        />
        <Route
          path="/billing/success"
          element={
            <AuthGuard>
              <BillingSuccessPage />
            </AuthGuard>
          }
        />
        <Route path="/billing/cancel" element={<BillingCancelPage />} />

        {/* Invite acceptance — auth required; if missing, the page redirects to /auth?next=… */}
        <Route path="/invite" element={<InvitePage />} />

        {/* Catch-all → home */}
        <Route path="*" element={<LandingPage />} />
      </Routes>
    </Suspense>
  );
}
